export const REGISTRY_SCENARIO_PREFIX = 'scenario:';

/** Legacy preset ids that map 1:1 to registry slugs after migration. */
export const LEGACY_SCENARIO_SLUGS = {
  mma_test: 'mma_test',
  btc_test: 'btc_test',
  jean_test: 'jean_test',
  equity_portfolio_1x: 'equity_portfolio_1x',
  equity_portfolio_225x: 'equity_portfolio_225x',
  og_equity_portfolio_1x: 'og_equity_portfolio_1x',
  og_equity_portfolio_225x: 'og_equity_portfolio_225x',
  vc_core_standard: 'vc_core_standard',
  vc_core_aggressive: 'vc_core_aggressive',
  vector_capital_journal: 'vector_capital_journal',
  master_multi_account: 'master_multi_account',
};

export function registryScenarioId(slug) {
  return `${REGISTRY_SCENARIO_PREFIX}${String(slug || '').trim()}`;
}

export function parseRegistryScenarioId(id) {
  const raw = String(id || '');
  if (!raw.startsWith(REGISTRY_SCENARIO_PREFIX)) return null;
  return raw.slice(REGISTRY_SCENARIO_PREFIX.length).trim() || null;
}

/** Accept legacy preset ids and normalize to scenario:slug when known. */
export function normalizeScenarioId(id) {
  const raw = String(id || '').trim();
  if (!raw) return raw;
  if (raw.startsWith(REGISTRY_SCENARIO_PREFIX)) return raw;
  const slug = LEGACY_SCENARIO_SLUGS[raw];
  if (slug) return registryScenarioId(slug);
  return raw;
}

export function legacyPresetIdFromRegistrySlug(slug) {
  const key = String(slug || '').trim();
  const match = Object.entries(LEGACY_SCENARIO_SLUGS).find(([, value]) => value === key);
  return match ? match[0] : null;
}

export function registryScenarioLabel(scenario, fallback) {
  return scenario?.displayName?.trim() || fallback;
}

export function registryScenarioColor(scenario, fallback) {
  return scenario?.displayColor?.trim() || fallback;
}

export const SCENARIO_CATEGORY_LABELS = {
  tests: 'Test',
  equity_algos: 'Individual stock algo',
  futures: 'Futures',
  stocks: 'Stock options',
};

/** UI-only webhook flavor ids — stored in DB as data_source_type = webhook + parser. */
export const WEBHOOK_FLAVOR_IDS = [
  'webhook_tradingview',
  'webhook_kairos',
  'webhook_fyntrak',
  'webhook_nt8_alert',
  'webhook_trade_fills',
];

export const DATA_SOURCE_TYPE_LABELS = {
  equity_curve: 'Daily equity curve (CSV)',
  trade_journal: 'Trade journal (CSV)',
  futures_snapshot: 'Futures strategy snapshot',
  portfolio_blend: 'Portfolio blend',
  nt_connector: 'NT8 Connector (live sync)',
  webhook_tradingview: 'Live webhook — TradingView strategy orders',
  webhook_kairos: 'Live webhook — Kairos daily equity',
  webhook_fyntrak: 'Live webhook — Fyntrak / MMA',
  webhook_nt8_alert: 'Live webhook — NinjaTrader Trade Alert',
  webhook_trade_fills: 'Live webhook — individual trades (each buy and sell)',
};

export const WEBHOOK_PARSER_BY_FLAVOR = {
  webhook_tradingview: 'tradingview_orders',
  webhook_kairos: 'kairos_daily',
  webhook_fyntrak: 'fyntrak_daily',
  webhook_nt8_alert: 'nt8_trade_alert',
  webhook_trade_fills: 'trade_fills',
};

export const WEBHOOK_FLAVOR_HINTS = {
  webhook_tradingview: 'POST JSON alerts from TradingView (strategy_id, action, executed_price, etc.). Entry and exit can arrive as separate alerts.',
  webhook_kairos: 'POST daily equity snapshots from TradingView/Kairos: { "strategy_id": "QLD" or "TQQQ", "date": "YYYY-MM-DD", "equity": 123456.78 }. Upload a CSV equity baseline first.',
  webhook_fyntrak: 'POST Fyntrak JSON payloads with strategy_orders (and optional ninja_open_positions). Same shape as the legacy /api/webhooks/fyntrak feed.',
  webhook_nt8_alert: 'POST NT8 Trade Alert webhooks: { "username": "Trade Alert", "content": "..." }. Optional TradesViz CSV sets the historical baseline; live NT8 alerts for your mapped strategy stitch on after the last CSV trade (or from the connect date if there is no CSV yet).',
  webhook_trade_fills: 'POST each buy and sell: { "type": "trade", "symbol": "NVDA", "action": "buy", "quantity": 100, "price": 182.45, "time": "2026-10-17T14:32:00Z" }. RiskLo pairs them first-in-first-out per symbol into closed trades and keeps the rest as open positions.',
};

export const KAIROS_SYMBOL_OPTIONS = [
  { id: '', label: 'No symbol — mixed / custom' },
  { id: 'QLD', label: 'QLD / Kairos Standard' },
  { id: 'TQQQ', label: 'TQQQ / Kairos Aggressive' },
];

export const CATEGORY_DATA_TYPES = {
  tests: [
    'equity_curve',
    'trade_journal',
    'webhook_tradingview',
    'webhook_kairos',
    'webhook_fyntrak',
    'webhook_nt8_alert',
  ],
  equity_algos: ['equity_curve', 'trade_journal'],
  futures: ['futures_snapshot', 'trade_journal', 'webhook_nt8_alert'],
  stocks: [
    'equity_curve',
    'portfolio_blend',
    'trade_journal',
    'webhook_kairos',
  ],
};

export function isWebhookFlavor(dataSourceKey) {
  return WEBHOOK_FLAVOR_IDS.includes(String(dataSourceKey || '').trim());
}

export function webhookParserForFlavor(dataSourceKey) {
  return WEBHOOK_PARSER_BY_FLAVOR[String(dataSourceKey || '').trim()] || null;
}

/** Map UI data source selection to API create payload fields. */
export function resolveScenarioCreateDataSource(dataSourceKey, dataSourceConfig = {}) {
  if (isWebhookFlavor(dataSourceKey)) {
    return {
      dataSourceType: 'webhook',
      dataSourceConfig: {
        ...dataSourceConfig,
        parser: webhookParserForFlavor(dataSourceKey),
      },
    };
  }
  return {
    dataSourceType: dataSourceKey,
    dataSourceConfig,
  };
}

/** Map stored strategy row to UI data-source dropdown value. */
export function dataSourceUiKeyFromScenario(scenario) {
  if (!scenario) return 'equity_curve';
  const type = String(scenario.dataSourceType || scenario.data_source_type || '').trim();
  const config = scenario.dataSourceConfig || scenario.data_source_config || {};
  if (type === 'webhook') {
    const parser = String(config.parser || '').trim();
    const match = Object.entries(WEBHOOK_PARSER_BY_FLAVOR).find(([, p]) => p === parser);
    return match ? match[0] : 'webhook_tradingview';
  }
  return type || 'equity_curve';
}

export function slugFromDisplayName(name) {
  const base = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  if (!base) return '';
  return (/^[0-9]/.test(base) ? `s_${base}` : base).slice(0, 64);
}

/** Strategy types that cannot use optional NT8 Trade Alert overlay on CSV baseline. */
export const NT8_OVERLAY_EXCLUDED_DATA_TYPES = ['nt_connector', 'portfolio_blend'];

export function isNt8OverlayExcludedDataType(dataSourceType) {
  return NT8_OVERLAY_EXCLUDED_DATA_TYPES.includes(String(dataSourceType || '').trim());
}

/** UI data-source key (includes webhook flavors). */
export function supportsNt8LiveOverlayUiKey(dataSourceUiKey) {
  const key = String(dataSourceUiKey || '').trim();
  if (!key || key === 'nt_connector' || key === 'portfolio_blend') return false;
  if (key === 'webhook_nt8_alert') return false;
  return true;
}

/** Stored DB data_source_type (webhook is generic). */
export function supportsNt8LiveOverlayStoredType(dataSourceType, dataSourceConfig = {}) {
  if (isNt8OverlayExcludedDataType(dataSourceType)) return false;
  if (dataSourceType === 'webhook' && String(dataSourceConfig.parser || '').trim() === 'nt8_trade_alert') {
    return false;
  }
  return true;
}
